/**
 * Sprint 22 — client-side PDF export for RAID analysis modals.
 *
 * Renders a DOM subtree to PDF via html2canvas (rasterize) + jsPDF
 * (paginate + save). Pure frontend: no server load, works offline.
 *
 * Output is image-based (not selectable text). Acceptable trade-off
 * for analysis snapshots that include charts and computed cells —
 * a server-side text-PDF generator would need its own templates per
 * analysis type, which is much bigger scope.
 */
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

interface ExportPdfOptions {
  /** ID of the DOM element to capture. The element should be currently visible. */
  elementId: string
  /** Filename without extension. Will get `.pdf` appended. */
  filename: string
  /** Optional title rendered as page header. */
  title?: string
}

/**
 * Capture the element by id and download the result as a paginated PDF.
 *
 * Throws if the element isn't found. Caller should wrap in try/catch
 * and surface failures to the user via toast.
 */
export async function exportAnalysisPdf({
  elementId,
  filename,
  title,
}: ExportPdfOptions): Promise<void> {
  const element = document.getElementById(elementId)
  if (!element) {
    throw new Error(`Element #${elementId} not found`)
  }

  // 2× DPR keeps lines / small text crisp on high-res prints without
  // ballooning file size like 3× would. html2canvas defaults to 1.
  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
  })

  // A4 portrait in mm: 210 × 297. We work in mm throughout so the
  // pagination math is in printer units, not pixels.
  const pdf = new jsPDF('portrait', 'mm', 'a4')
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 10
  const usableWidth = pageWidth - margin * 2

  // Maintain aspect ratio when scaling the canvas to page width.
  const imgWidth = usableWidth
  const imgHeight = (canvas.height * imgWidth) / canvas.width

  // Optional title at the top of page 1.
  let cursorY = margin
  if (title) {
    pdf.setFontSize(14)
    pdf.text(title, margin, cursorY + 5)
    cursorY += 12
  }

  // Paginate: jsPDF doesn't auto-split a tall image, so we render the
  // SAME image at increasingly negative Y offsets, clipping to the
  // page each time.
  const dataUrl = canvas.toDataURL('image/png')
  const slicePerPage = pageHeight - margin * 2 - (title ? 12 : 0)
  let remainingHeight = imgHeight
  let pageOffsetY = cursorY

  let yInImage = 0
  while (remainingHeight > 0) {
    pdf.addImage(
      dataUrl,
      'PNG',
      margin,
      pageOffsetY - yInImage,
      imgWidth,
      imgHeight,
    )
    remainingHeight -= slicePerPage
    yInImage += slicePerPage
    if (remainingHeight > 0) {
      pdf.addPage()
      pageOffsetY = margin
    }
  }

  pdf.save(`${filename}.pdf`)
}
